import { Menu, Tab, Transition } from "@headlessui/react"
import React, { Fragment, useState } from "react"
import { useTranslation } from "react-i18next"
import { HiDownload, HiEyeOff, HiOutlineDotsHorizontal, HiOutlineRefresh, HiShare } from "react-icons/hi"
import { useHistory } from "react-router-dom"
import { toast } from "react-toastify"

import placeholder from "../../../assets/destination-placeholder.jpg"
import type { ConfirmationState, Destination, PaginationInfo } from "../../../constants/types"
import { getLocaleValue, parseImage, twClassNames } from "../../../helpers"
import { useTeams } from "../../../hooks/useTeams"
import { useDeleteDestination, useDownloadPdf } from "../../../hooks/useDestinations"
import { useSelectedProject } from "../../../contexts/SelectedProjectContext"
import ListFooter from "./listFooter"

const SHARE_LINK = "https://www.gettips.fi/handbook/"

const ThumbnailImage: React.FC<{ image?: string | null }> = ({ image }) => (
  <img
    width={200}
    height={200}
    className="tw-h-[200px] tw-w-[200px] tw-max-w-[200px] tw-rounded-bl-2xl tw-rounded-tl-2xl tw-object-cover"
    alt="destination cover"
    src={image ? parseImage(image) : placeholder}
  />
)

type HandbookProps = {
  destination: Destination
  onSelect: (dest: Destination) => void
  onDelete: (dest: Destination) => void
  onShareLink: (accessCode: string) => void
  onDownloadPdf: (destinationId: string, type: "guidebook" | "brochure") => void
  isDownloading: { guidebook: boolean; brochure: boolean }
}

const Handbook: React.FC<HandbookProps> = ({
  destination,
  onSelect,
  onDelete,
  onShareLink,
  onDownloadPdf,
  isDownloading
}) => {
  const { t, i18n } = useTranslation()
  const { data: teams = [] } = useTeams()

  const language = i18n.resolvedLanguage ?? "en"
  const { isPublic, image, streetName, postalCode, city, country, phone, accessCode, ownerTeamId } = destination

  return (
    <div
      className="tw-mb-4 tw-flex tw-flex-col tw-rounded-2xl tw-border tw-border-gray tw-bg-white tw-shadow
        md:tw-flex-row">
      {/* Image */}
      <div className="tw-hidden tw-h-[200px] tw-w-full tw-max-w-[200px] tw-bg-gray lg:tw-block">
        <ThumbnailImage image={image} />
      </div>

      {/* Content */}
      <div className="tw-flex tw-w-full tw-flex-col tw-px-6 tw-py-4">
        {/* Header */}
        <div className="tw-mb-2 tw-flex tw-items-start tw-gap-2">
          <div className="tw-flex tw-grow tw-items-center tw-gap-2">
            <div className="tw-truncate tw-text-lg tw-font-semibold">
              {getLocaleValue(destination, "name", language)}
            </div>

            {/* Visibility badge: show only when NOT public */}
            {!isPublic && (
              <span
                className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-red-50 tw-px-2.5
                  tw-py-1 tw-text-xs tw-font-medium tw-text-red-700 tw-ring-1 tw-ring-inset tw-ring-red-200"
                title={t("projects.visibility.hidden", "Hidden")}
                aria-label={t("projects.visibility.hidden", "Hidden")}>
                <HiEyeOff className="tw-h-4 tw-w-4" />
                {t("projects.visibility.hidden", "Hidden")}
              </span>
            )}
          </div>

          {/* Kebab menu */}
          <Menu as="div" className="tw-relative tw-inline-block tw-text-left">
            <Menu.Button className="tw-inline-flex tw-w-full tw-justify-end tw-px-2">
              <HiOutlineDotsHorizontal className="tw-h-5 tw-w-5" aria-hidden="true" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="tw-transition tw-ease-out tw-duration-100"
              enterFrom="tw-transform tw-opacity-0 tw-scale-95"
              enterTo="tw-transform tw-opacity-100 tw-scale-100"
              leave="tw-transition tw-ease-in tw-duration-75"
              leaveFrom="tw-transform tw-opacity-100 tw-scale-100"
              leaveTo="tw-transform tw-opacity-0 tw-scale-95">
              <Menu.Items
                className="tw-absolute tw-right-0 tw-z-10 tw-w-56 tw-origin-top-right tw-rounded-md tw-border
                  tw-border-dark-15 tw-bg-white tw-py-1 tw-shadow-lg focus:tw-outline-none">
                <Menu.Item>
                  {({ active }) => (
                    <div
                      onClick={() => onDownloadPdf(destination.id, "guidebook")}
                      className={twClassNames(
                        active ? "tw-bg-gray" : "",
                        "tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-text-dark hover:tw-no-underline",
                        isDownloading.guidebook && "tw-pointer-events-none tw-opacity-50"
                      )}>
                      <HiDownload className="tw-h-4 tw-w-4" />
                      {t("projects.button.downloadGuidebook")}
                    </div>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <div
                      onClick={() => onDownloadPdf(destination.id, "brochure")}
                      className={twClassNames(
                        active ? "tw-bg-gray" : "",
                        "tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-text-dark hover:tw-no-underline",
                        isDownloading.brochure && "tw-pointer-events-none tw-opacity-50"
                      )}>
                      <HiDownload className="tw-h-4 tw-w-4" />
                      {t("projects.button.downloadBrochure", "Download Brochure PDF")}
                    </div>
                  )}
                </Menu.Item>
                <div className="tw-my-1 tw-border-t tw-border-gray" />
                <Menu.Item>
                  {({ active }) => (
                    <div
                      onClick={() => onDelete(destination)}
                      className={twClassNames(
                        active ? "tw-bg-gray" : "tw-text-red-700",
                        "tw-block tw-px-4 tw-py-2 tw-text-sm hover:tw-text-red-700 hover:tw-no-underline"
                      )}>
                      {t("projects.button.delete")}
                    </div>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>

        {/* Body */}
        <div className="tw-flex tw-grow tw-flex-col md:tw-flex-row">
          {/* Left */}
          <div className="tw-flex tw-w-full tw-flex-col md:tw-mr-6 lg:tw-mr-12">
            <div className="tw-my-2 tw-flex tw-flex-col tw-text-sm md:tw-flex-row">
              <div className="tw-w-full tw-text-sm tw-text-dark md:tw-w-1/2">
                <span className="tw-font-medium tw-text-dark">
                  {streetName} {postalCode}
                </span>
                <span className="tw-mx-2 tw-text-dark-25">|</span>
                <span className="tw-capitalize tw-text-dark-50">
                  {city}, {country}
                </span>
              </div>
            </div>
            <div className="tw-line-clamp-2 tw-text-sm tw-text-dark-50">
              {getLocaleValue(destination, "description", language)}
            </div>
            <div className="tw-mt-auto tw-flex tw-items-center tw-justify-between tw-pt-3">
              <div
                className="tw-inline-flex tw-items-center tw-rounded tw-bg-gray-100 tw-px-3 tw-py-1 tw-text-sm
                  tw-font-medium tw-text-dark-75">
                {teams.find(team => team.id === ownerTeamId)?.name}
              </div>
              {phone && <div className="tw-text-sm tw-text-dark-50">{phone}</div>}
            </div>
          </div>
          {/* Right */}
          <div className="tw-flex tw-flex-col md:tw-w-1/3">
            <div
              className="tw-flex tw-w-full tw-cursor-pointer tw-rounded-xl tw-bg-light tw-px-4 tw-py-2 max-md:tw-mt-4"
              onClick={() => onShareLink(accessCode)}>
              <div className="tw-flex tw-grow tw-flex-col tw-text-left">
                <span className="tw-text-xs tw-uppercase tw-text-dark-50">{t("projects.accessCode")}</span>
                <span>{accessCode}</span>
              </div>
              <div className="tw-flex tw-items-center tw-text-right">
                <HiShare className="tw-text-xl" />
              </div>
            </div>
            <div className="tw-flex tw-grow tw-items-end max-md:tw-mt-4">
              <button className="tw-btn tw-btn-primary tw-w-full" onClick={() => onSelect(destination)}>
                {t("projects.button.select")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

type TabHandbooksProps = {
  destinations: Destination[]
  query: string
  pagination: PaginationInfo
  page: number
  maxRows: number
  onPageChange: (page: number) => void
  onMaxRowSelect: (n: number) => void
  setConfirmation: (c: ConfirmationState) => void
  hideConfirmation: () => void
  isLoading?: boolean
}

const TabHandbooks: React.FC<TabHandbooksProps> = ({
  destinations,
  query,
  pagination: serverPagination,
  page,
  maxRows,
  onPageChange,
  onMaxRowSelect,
  setConfirmation,
  hideConfirmation,
  isLoading
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { setSelectedDestinationId } = useSelectedProject()
  const deleteDestinationMutation = useDeleteDestination()
  const downloadPdfMutation = useDownloadPdf()
  const [downloading, setDownloading] = useState<Record<string, { guidebook: boolean; brochure: boolean }>>({})

  // Build pagination state compatible with ListFooter from server pagination
  const paginationState = {
    selected: page,
    min: (page - 1) * maxRows,
    max: Math.min(page * maxRows, serverPagination.total)
  }

  const onSelect = (destination: Destination) => {
    setSelectedDestinationId(destination.id)
    history.push("/handbook/guides")
  }

  const onDelete = (id: string) =>
    setConfirmation({
      show: true,
      callback: () => {
        hideConfirmation()
        deleteDestinationMutation.mutate(id)
      },
      title: "delete",
      content: "delete.destination",
      danger: true
    })

  const onCopyLinkToClipboard = (accessCode: string) => {
    toast.success(t("projects.notifications.copiedShareLink"))
    void navigator.clipboard.writeText(SHARE_LINK + accessCode)
  }

  const onDownloadPdf = async (destinationId: string, type: "guidebook" | "brochure") => {
    setDownloading(prev => ({
      ...prev,
      [destinationId]: { ...(prev[destinationId] ?? { guidebook: false, brochure: false }), [type]: true }
    }))

    const toastId = toast.loading(t("projects.notifications.pdfGenerating", "Generating PDF..."))

    try {
      await downloadPdfMutation.mutateAsync({ destinationId, type })
      toast.update(toastId, {
        render: t("projects.notifications.pdfDownloaded", "PDF downloaded successfully"),
        type: "success",
        isLoading: false,
        autoClose: 3000
      })
    } catch (error) {
      console.error("PDF download failed:", error)
      toast.update(toastId, {
        render: t("projects.notifications.pdfError", "Failed to download PDF"),
        type: "error",
        isLoading: false,
        autoClose: 5000
      })
    } finally {
      setDownloading(prev => ({
        ...prev,
        [destinationId]: { ...(prev[destinationId] ?? { guidebook: false, brochure: false }), [type]: false }
      }))
    }
  }

  return (
    <Tab.Panel>
      {/* Loading */}
      {isLoading && (
        <div className="tw-my-12 tw-flex tw-flex-col tw-items-center tw-justify-center">
          <HiOutlineRefresh className="tw-h-10 tw-w-10 tw-animate-spin tw-text-dark-50" />
        </div>
      )}

      {/* Empty */}
      {!isLoading && destinations.length === 0 && (
        <div className="tw-my-12 tw-flex tw-flex-col tw-items-center tw-justify-center">
          <p className="tw-mb-0 tw-text-center tw-text-lg tw-text-dark-50">
            {query ? t("projects.empty.withQuery.handbooks") : t("projects.empty.withoutQuery.handbooks")}
          </p>
          {query === "" && (
            <button
              className="tw-btn tw-btn-outline tw-mt-6 tw-min-w-[180px]"
              onClick={() => history.push("/handbook/create")}>
              <span>{t("projects.button.create.handbook")}</span>
            </button>
          )}
        </div>
      )}

      {!isLoading && (
        <>
          {destinations.map(destination => (
            <Handbook
              destination={destination}
              key={destination.id}
              onSelect={dest => onSelect(dest)}
              onDelete={({ id }) => onDelete(id)}
              onShareLink={onCopyLinkToClipboard}
              onDownloadPdf={onDownloadPdf}
              isDownloading={downloading[destination.id] ?? { guidebook: false, brochure: false }}
            />
          ))}

          <ListFooter
            total={serverPagination.total}
            pagination={paginationState}
            onPaginationSelect={onPageChange}
            maxRows={maxRows}
            onMaxRowSelect={onMaxRowSelect}
          />
        </>
      )}
    </Tab.Panel>
  )
}

export default TabHandbooks
